// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import DBWorker from './worker/db.worker.ts?worker&inline';
import DBContainerManager, { DBResult, DBResultData } from './worker/db.container';
import { DB } from '../../typings/model/db';
import { IMsg } from '../../typings/model/im';
import { report } from '../api/report';
import { logger } from '../../util/logger';
import { Timer } from '../../util/timer';
import { NoReportError } from '../../util/error';

export interface IWorkerPostMessage {
  id: string;
  func: string;
  params: Array<unknown>;
}

export interface IWorkerResult {
  id: string;
  data: DBResult<unknown>;
}

function monitor() {
  return function (target: DBManager, methodName: string, descriptor: PropertyDescriptor) {
    const oldValue = descriptor.value;
    // eslint-disable-next-line no-param-reassign
    descriptor.value = async function (...args: unknown[]) {
      try {
        const timer = Timer.getInstance();
        const timerId = `${methodName}${Date.now()}`;
        timer.start(timerId);
        const result = await oldValue.apply(this, args);
        const { duration, pageInvisibleDuration } = timer.stop(timerId);
        const firstParam = args?.[0];
        const firstParamLength = Array.isArray(firstParam) ? firstParam.length : 1;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const ext5 = this.worker ? '1' : '0';
        report({
          reportParamsList: [{
            operId: '2017000540001',
            ext1: methodName,
            ext2: firstParamLength.toString(),
            ext3: duration.toString(),
            ext4: pageInvisibleDuration?.toString(),
            ext5,
          }],
        });
        return result;
      } catch (e) {
        logger.error('[DBManager]', methodName, e);
      }
    };
    return descriptor;
  };
}

export default class DBManager {
  private static instance: DBManager;

  public static getInstance(): DBManager {
    if (!DBManager.instance) {
      DBManager.instance = new DBManager();
    }
    return DBManager.instance;
  }

  private db: DBContainerManager | null = null;
  private worker: Worker | null = null;
  private callbackMap: Record<string, (result: DBResult<unknown>) => void> = {};
  constructor() {
    // 如果浏览器不支持 webWorker，那么就降级使用同进程方案
    if (window.Worker) {
      this.worker = new DBWorker();
      if (this.worker) {
        this.worker.onmessage = this.handlerMessage.bind(this);
        this.worker.onerror = () => {
          this.db = DBContainerManager.getInstance();
          Object.keys(this.callbackMap).forEach((callbackId) => {
            this.dispatchCallback({
              id: callbackId,
              data: {
                // 部分环境不支持worker，无需上报
                error: new NoReportError('init worker error'),
                data: null,
              },
            });
          });
          this.worker = null;
          logger.warn('[DBManager]', 'DBWorker error, use DBManager');
        };
      }
      logger.info('[DBManager]', 'use DBWorker');
    } else {
      this.db = DBContainerManager.getInstance();
      logger.info('[DBManager]', 'use DBManager');
    }
  }

  async sendMessage(func: string, params: Array<unknown>): Promise<DBResultData<unknown>> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const method = this.db?.[func].bind(this.db);
    if (method) {
      // 不确定方法是否为 async，先统一使用 await 调用，然后用 Promise 返回
      const result: DBResult<unknown> = await method(...params);
      if (result.error) {
        return Promise.reject(result.error);
      }
      return Promise.resolve(result.data);
    }
    return new Promise((resolve, reject) => {
      const callbackId = `id_${Date.now()}_${Math.random()}`;
      this.callbackMap[callbackId] = function (result: DBResult<unknown>) {
        if (result.error) {
          return reject(result.error);
        }
        return resolve(result.data);
      };
      this.worker?.postMessage({
        id: callbackId,
        func,
        params,
      });
    });
  }

  handlerMessage(event: MessageEvent) {
    this.dispatchCallback(event.data as IWorkerResult);
  }

  dispatchCallback(result: IWorkerResult) {
    const callback = this.callbackMap?.[result.id];
    if (callback) {
      callback(result.data);
      delete this.callbackMap[result.id];
    }
  }

  @monitor()
  async bulkPutUserInfo(userInfoList: Array<DB.IUserInfo>): Promise<DBResultData<boolean>> {
    return this.sendMessage('bulkPutUserInfo', [userInfoList]) as Promise<DBResultData<boolean>>;
  }

  @monitor()
  async getUserInfoList(userIdList: Array<string>): Promise<DBResultData<Array<DB.IUserInfo>>> {
    return this.sendMessage('getUserInfoList', [userIdList]) as Promise<DBResultData<Array<DB.IUserInfo>>>;
  }

  /**
   * 批量存入链接解析的数据，用于聊天场景使用
   * @param dataList 链接解析的内容集合
   * @returns 是否存入成功
   */
  @monitor()
  async bulkPutLinkMsgInfo(dataList: Array<DB.ILinkMsgInfo>): Promise<DBResultData<boolean>> {
    return this.sendMessage('bulkPutLinkMsgInfo', [dataList]) as Promise<DBResultData<boolean>>;
  }

  /**
   * 查询获取链接解析的数据
   * @param linkList 查询的链接集合
   * @returns 链接解析的内容集合
   */
  @monitor()
  async getLinkMsgList(linkList: Array<string>): Promise<DBResultData<Array<DB.ILinkMsgInfo>>> {
    return this.sendMessage('getLinkMsgList', [linkList]) as Promise<DBResultData<Array<DB.ILinkMsgInfo>>>;
  }

  @monitor()
  async bulkPutMessageList(channelId: string, dataList: Array<IMsg>): Promise<DBResultData<boolean>> {
    return this.sendMessage('bulkPutMessageList', [channelId, dataList]) as Promise<DBResultData<boolean>>;
  }

  @monitor()
  async getMessageList(channelId: string): Promise<DBResultData<Array<IMsg>>> {
    return this.sendMessage('getMessageList', [channelId]) as Promise<DBResultData<Array<IMsg>>>;
  }

  @monitor()
  async getLastMessage(channelIdList: Array<string>): Promise<DBResultData<Record<string, IMsg>>> {
    return this.sendMessage('getLastMessage', [channelIdList]) as Promise<DBResultData<Record<string, IMsg>>>;
  }

  async restart(): Promise<DBResultData<boolean>> {
    return this.sendMessage('restart', []) as Promise<DBResultData<boolean>>;
  }
}
